import axios, { AxiosResponse } from "axios";
import {
  APP_ASSESSMENTS,
  APP_ASSESSMENTS_RESULTS,
  FETCH_ASSESSMENT,
  FETCH_ASSESSMENT_PDF,
  FETCH_ASSESSMENT_SUMMARY,
  FETCH_NOTIFICATIONS,
  FETCH_NOTIFICATIONS_COUNT,
  FETCH_SKILLS,
  FETCH_SKILLS_GROUP,
  FETCH_USER_WITH_PROFILE,
  FETCH_USERS_INFO,
  FETCH_USERS_WITH_NAME_AND_EID,
  GET_JOB_BY_ID,
  GET_ROLES_LIST,
  GET_UNIQUE_SECTORS,
  MARK_ASSESSMENT_RESUMED,
  REVERT_ASSESSMENT_TO_IN_PROGRESS,
  NOTIFICATIONS_ACTION,
  SAVE_ASSESSMENT_STATE,
  SEND_EMAIL,
  UPLOAD_FILE_CHUNK
} from "@/store/urls/common";
import {
  AppAssessmentResult,
  FetchNotificationListPayload,
  FetchSkillsGroupActionPayload,
  SendEmailPayload,
  SkillSearchPayload,
  UpdateNotificationStatusPayload
} from "@/store/modules/common/interfaces";

export const get_unique_sectors = async (): Promise<AxiosResponse> => {
  return await axios.get(GET_UNIQUE_SECTORS).then((response) => response.data);
};

export const get_roles_list = async (): Promise<AxiosResponse> => {
  return await axios.get(GET_ROLES_LIST);
};
export const get_job_by_id = async (job_id: number): Promise<AxiosResponse> => {
  return await axios.get(`${GET_JOB_BY_ID}${job_id}`);
};

export const fetch_skills_group = async (
  payload: FetchSkillsGroupActionPayload
): Promise<AxiosResponse> => {
  return await axios.post(FETCH_SKILLS_GROUP, payload);
};

export const fetch_skills = async (
  payload: SkillSearchPayload
): Promise<AxiosResponse> => {
  return await axios.post(FETCH_SKILLS, payload);
};

export const fetch_user_info = async (name: string): Promise<AxiosResponse> => {
  return await axios.get(`${FETCH_USERS_INFO}${name}`);
};

export const fetch_user_with_profile = async (
  uid: number
): Promise<AxiosResponse> => {
  return await axios.get(`${FETCH_USER_WITH_PROFILE}${uid}`);
};

export const get_notifications_count = async (): Promise<AxiosResponse> => {
  return await axios.get(FETCH_NOTIFICATIONS_COUNT);
};

export const get_notifications_list = async (
  payload: FetchNotificationListPayload
): Promise<AxiosResponse> => {
  const page = payload.page;
  const limit = payload.limit;
  let query = `?page=${page}&limit=${limit}&`;
  if (payload.is_read) query += `is_read=${payload.is_read}`;
  return await axios.get(`${FETCH_NOTIFICATIONS}${query}`);
};

export const update_notifications_status = async (
  payload: UpdateNotificationStatusPayload
): Promise<AxiosResponse> => {
  return await axios.post(`${NOTIFICATIONS_ACTION}`, payload);
};

export const fetch_assessment_summary = async (
  assessment_id: number
): Promise<AxiosResponse> => {
  return await axios.get(`${FETCH_ASSESSMENT_SUMMARY}${assessment_id}`);
};

export const fetch_assessment_pdf = async (
  assessment_id: number
): Promise<AxiosResponse> => {
  return await axios.get(`${FETCH_ASSESSMENT_PDF}${assessment_id}`);
};

export const fetch_assessment = async (
  user_id: number
): Promise<AxiosResponse> => {
  return await axios.get(`${FETCH_ASSESSMENT}${user_id}`);
};

export const app_assessments = async (): Promise<AxiosResponse> => {
  return await axios.get(`${APP_ASSESSMENTS}`);
};

export const app_assessments_result = async (
  payload: AppAssessmentResult
): Promise<AxiosResponse> => {
  // console.log("Getting result from common.ts: ", payload);
  let query = `${payload.assessment_id}/result/`;
  if (payload.user_id) query += `${payload.user_id}`;
  // const result = await axios.get(`${APP_ASSESSMENTS_RESULTS}${query}`);
  // console.log("Result: ", axios.get(`${APP_ASSESSMENTS_RESULTS}${query}`));
  return await axios.get(`${APP_ASSESSMENTS_RESULTS}${query}`);
  // return result;
};

export const mark_assessment_resumed = async (
  assessment_id: number
): Promise<AxiosResponse> => {
  return await axios.put(MARK_ASSESSMENT_RESUMED(assessment_id));
};

export const revert_assessment_to_in_progress = async (
  assessment_id: number
): Promise<AxiosResponse> => {
  return await axios.put(REVERT_ASSESSMENT_TO_IN_PROGRESS(assessment_id));
};

export const save_assessment_state = async (
  assessment_id: number,
  saved_state: Record<string, unknown>,
  time_spent?: number,
  is_starting?: boolean
): Promise<AxiosResponse> => {
  return await axios.post(SAVE_ASSESSMENT_STATE(assessment_id), {
    saved_state,
    time_spent,
    is_starting
  });
};

export const app_assessments_result_pdf = async (
  payload: AppAssessmentResult
): Promise<string> => {
  let query = `${payload.assessment_id}/result/`;
  if (payload.user_id) query += `${payload.user_id}/pdf`;

  // Backend serves PDF directly or returns S3 URL
  // Try to fetch the response and check if it's JSON or binary
  const response = await axios.get(`${APP_ASSESSMENTS}${query}`, {
    responseType: "blob",
    validateStatus: (status) => status >= 200 && status < 300
  });

  // Check if response is PDF (binary) or JSON
  const contentType = response.headers["content-type"];

  if (contentType && contentType.includes("application/pdf")) {
    // Binary PDF response - create blob URL
    const blob = new Blob([response.data], { type: "application/pdf" });
    return URL.createObjectURL(blob);
  } else if (contentType && contentType.includes("application/json")) {
    // JSON response with S3 URL - parse and return
    const text = await response.data.text();
    const json = JSON.parse(text);
    return json.data;
  } else {
    // Fallback: return direct URL
    return `${APP_ASSESSMENTS}${query}`;
  }
};

export const send_email = async (
  payload: SendEmailPayload
): Promise<AxiosResponse> => {
  return await axios.post(`${SEND_EMAIL}`, payload);
};

export const upload_file_chunk = async (
  payload: FormData
): Promise<AxiosResponse> => {
  return await axios.post(`${UPLOAD_FILE_CHUNK}`, payload);
};

export const fetch_user_by_name_and_eid = async (
  name: string,
  signal?: AbortSignal
): Promise<AxiosResponse> => {
  return await axios.get(`${FETCH_USERS_WITH_NAME_AND_EID}${name}`, {
    signal
  });
};
